// src/components/dashboard/WatchlistSection/WatchlistSection.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../../utils/constants';
import { useCurrency } from '../../../context/CurrencyContext';
import './WatchlistSection.css';

/**
 * Map a price change percentage to a colour class.
 *   green  → +1% or more
 *   yellow → 0% to +1%
 *   orange → -1% to 0%
 *   red    → -1% or less
 */
const getBarColorClass = (pct) => {
  if (pct >= 1)  return 'green';
  if (pct >= 0)  return 'yellow';
  if (pct >= -1) return 'orange';
  return 'red';
};

// ─────────────────────────────────────────────────────────────
// Mini 12-bar preview chart
// Uses price_history_preview from the API (oldest→newest order).
// Falls back to flat placeholder bars if no history available.
// ─────────────────────────────────────────────────────────────
const MiniBarChart = React.memo(({ stock }) => {
  const history = stock.price_history_preview || [];

  const bars = useMemo(() => {
    if (history.length >= 2) {
      return history.slice(-12).map((point, i, arr) => {
        const price = parseFloat(point.price);
        const prevPrice = i > 0 ? parseFloat(arr[i - 1].price) : price;
        const pct = prevPrice !== 0 ? ((price - prevPrice) / prevPrice) * 100 : 0;
        return { price, colorClass: getBarColorClass(i === 0 ? 0 : pct) };
      });
    }
    // Placeholder: 12 flat green bars
    const fallbackPrice = parseFloat(stock.current_price) || 1;
    return Array.from({ length: 12 }, () => ({ price: fallbackPrice, colorClass: 'green' }));
  }, [history, stock.current_price]);

  // Scale bar heights 20%–100% relative to the price range in this set
  const prices = bars.map(b => b.price);
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const range = max - min || 1;

  return (
    <div className="watchlist-chart-preview">
      {bars.map((bar, i) => {
        const heightPct = 20 + ((bar.price - min) / range) * 80;
        return (
          <div
            key={i}
            className={`mini-bar ${bar.colorClass}`}
            style={{ height: `${heightPct}%` }}
          />
        );
      })}
    </div>
  );
});

MiniBarChart.displayName = 'MiniBarChart';

// ─────────────────────────────────────────────────────────────
// Single watchlist card
// ─────────────────────────────────────────────────────────────
const WatchlistCard = React.memo(({ item, onClick }) => {
  const { formatCurrency } = useCurrency();
  const stock = item.stock;

  const imageUrl = useMemo(() => {
    if (!stock.image) return null;
    if (stock.image.startsWith('http://') || stock.image.startsWith('https://')) return stock.image;
    const baseUrl = API_BASE_URL.replace('/api', '');
    const cleanPath = stock.image.startsWith('/') ? stock.image.slice(1) : stock.image;
    return `${baseUrl}/${cleanPath}`;
  }, [stock.image]);

  const priceChange = parseFloat(stock.price_change) || 0;
  const changeClass = priceChange > 0 ? 'positive' : priceChange < 0 ? 'negative' : 'neutral';
  const displayPrice = stock.current_price_display || formatCurrency(parseFloat(stock.current_price) || 0);

  return (
    <div className="watchlist-card" onClick={onClick}>
      {/* Top row: image + info + price */}
      <div className="watchlist-card-top">
        <div className="watchlist-stock-image-container">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={stock.symbol}
              className="watchlist-stock-image"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentElement.innerHTML =
                  `<span class="watchlist-stock-placeholder">${stock.symbol.charAt(0)}</span>`;
              }}
            />
          ) : (
            <span className="watchlist-stock-placeholder">{stock.symbol.charAt(0)}</span>
          )}
        </div>

        <div className="watchlist-stock-info">
          <div className="watchlist-stock-symbol">{stock.symbol}</div>
          <div className="watchlist-stock-name">{stock.name}</div>
        </div>

        <div className="watchlist-price-section">
          <div className="watchlist-current-price">{displayPrice}</div>
          <div className={`watchlist-price-change ${changeClass}`}>
            {priceChange > 0 ? '+' : ''}{priceChange.toFixed(2)}%
          </div>
        </div>
      </div>

      {/* Mini bar chart preview */}
      <MiniBarChart stock={stock} />

      <div className="watchlist-card-hint">Tap to view full chart →</div>
    </div>
  );
});

WatchlistCard.displayName = 'WatchlistCard';

// ─────────────────────────────────────────────────────────────
// Main WatchlistSection
// ─────────────────────────────────────────────────────────────
const WatchlistSection = ({ watchlist, loading }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const navigate = useNavigate();

  // Reset index if watchlist shrinks below current index
  useEffect(() => {
    if (watchlist && currentIndex >= watchlist.length && watchlist.length > 0) {
      setCurrentIndex(watchlist.length - 1);
    }
  }, [watchlist, currentIndex]);

  const nextSlide = useCallback(() => {
    if (!watchlist || watchlist.length <= 1) return;
    setCurrentIndex(prev => (prev + 1) % watchlist.length);
  }, [watchlist]);

  // Auto-advance every 10 seconds
  useEffect(() => {
    if (!watchlist || watchlist.length <= 1) return;
    const id = setInterval(nextSlide, 10000);
    return () => clearInterval(id);
  }, [nextSlide, watchlist]);

  const handleCardClick = useCallback((symbol) => {
    navigate(`/watchlist/${symbol}`);
  }, [navigate]);

  if (loading) {
    return (
      <div className="watchlist-section">
        <h2>Watchlist</h2>
        <div className="watchlist-loading">Loading watchlist...</div>
      </div>
    );
  }

  if (!watchlist || watchlist.length === 0) {
    return (
      <div className="watchlist-section">
        <h2>Watchlist</h2>
        <div className="watchlist-empty">
          <div className="watchlist-empty-icon">
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" fill="currentColor"/>
            </svg>
          </div>
          <div className="watchlist-empty-content">
            <h3>No stocks on your watchlist</h3>
            <p>Go to any stock detail page and tap the eye icon to start watching it here.</p>
          </div>
        </div>
      </div>
    );
  }

  // Single item — no carousel needed
  if (watchlist.length === 1) {
    return (
      <div className="watchlist-section">
        <h2>Watchlist</h2>
        <WatchlistCard
          item={watchlist[0]}
          onClick={() => handleCardClick(watchlist[0].stock.symbol)}
        />
      </div>
    );
  }

  // Multiple items — carousel
  return (
    <div className="watchlist-section">
      <div className="watchlist-header">
        <h2>Watchlist</h2>
        <div className="watchlist-counter">
          {currentIndex + 1} / {watchlist.length}
        </div>
      </div>

      <div className="watchlist-carousel">
        <div className="carousel-track">
          <div
            className="carousel-slides"
            style={{ transform: `translateX(-${currentIndex * 100}%)` }}
          >
            {watchlist.map((item) => (
              <div key={item.id} className="carousel-slide">
                <WatchlistCard
                  item={item}
                  onClick={() => handleCardClick(item.stock.symbol)}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Dot navigation */}
        <div className="watchlist-dots">
          {watchlist.map((_, index) => (
            <button
              key={index}
              className={`watchlist-dot ${index === currentIndex ? 'active' : ''}`}
              onClick={() => setCurrentIndex(index)}
              aria-label={`Go to watchlist item ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default WatchlistSection;